import { useAmadeusStatus } from '../../hooks/useAmadeusStatus';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface AmadeusStatusIndicatorProps {
  /** Whether the sidebar is collapsed */
  isCollapsed: boolean;
}

/**
 * Amadeus connection status indicator for the lawyer sidebar.
 *
 * Shows the current state of the Amadeus session:
 * - Green: Connected and valid
 * - Yellow (pulsing): Refreshing/connecting
 * - Red: Error or circuit breaker open
 * - Gray: Unknown/checking
 */
export function AmadeusStatusIndicator({ isCollapsed }: AmadeusStatusIndicatorProps) {
  const { status, loading } = useAmadeusStatus({
    pollInterval: 30000, // 30 seconds
  });

  const getIndicator = () => {
    if (loading || !status) {
      return {
        color: 'bg-gray-400',
        text: 'Checking...',
        description: 'Checking Amadeus connection status',
        pulse: true,
      };
    }

    if (status.circuit_breaker_open) {
      return {
        color: 'bg-red-500',
        text: 'Blocked',
        description: `Connection blocked after ${status.consecutive_failures} failures. Will retry at ${status.circuit_breaker_until ? new Date(status.circuit_breaker_until).toLocaleTimeString() : 'soon'}`,
        pulse: false,
      };
    }

    switch (status.state) {
      case 'valid':
        const ageText = status.session_age_seconds
          ? `Session age: ${Math.floor(status.session_age_seconds / 60)}m`
          : '';
        return {
          color: 'bg-green-500',
          text: 'Amadeus',
          description: `Connected. ${ageText}`,
          pulse: false,
        };
      case 'refreshing':
        return {
          color: 'bg-yellow-500',
          text: 'Connecting...',
          description: 'Creating new Amadeus session',
          pulse: true,
        };
      case 'error':
        return {
          color: 'bg-red-500',
          text: 'Error',
          description: status.error_message || 'Connection error',
          pulse: false,
        };
      default:
        return {
          color: 'bg-gray-400',
          text: 'Unknown',
          description: 'Status unknown',
          pulse: false,
        };
    }
  };

  const { color, text, description, pulse } = getIndicator();

  // Collapsed view: just the dot with tooltip
  if (isCollapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center py-3">
              <div
                className={`h-2 w-2 rounded-full ${color} ${pulse ? 'animate-pulse' : ''}`}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p className="font-medium">{text}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Expanded view: dot + text with tooltip for description
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 px-3 py-2 cursor-default">
            <div
              className={`h-2 w-2 rounded-full ${color} ${pulse ? 'animate-pulse' : ''} flex-shrink-0`}
            />
            <span className="text-xs text-sidebar-foreground/70 truncate">
              {text}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p>{description}</p>
          {status?.last_check_seconds_ago !== null && status?.last_check_seconds_ago !== undefined && (
            <p className="text-xs text-muted-foreground mt-1">
              Last checked: {status.last_check_seconds_ago}s ago
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default AmadeusStatusIndicator;
